import { useEffect, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { DEVICE_STORAGE_KEYS } from "../accountStorage";
import ThreadSidebar from "./Sidebar";
import { OperatorEntryBanner } from "./OperatorEntryBanner";
import DocumentsPanel from "./DocumentsSidebar";
import SubAgentPanel from "./SubAgentPanel";
import { useDocumentsPanelStore } from "../documentsPanelStore";
import { Sidebar, SidebarProvider, SidebarRail } from "./ui/sidebar";
import {
  clearShortcutModifierState,
  syncShortcutModifierStateFromKeyboardEvent,
} from "../shortcutModifierState";

const THREAD_SIDEBAR_WIDTH_STORAGE_KEY = DEVICE_STORAGE_KEYS.threadSidebarWidth;
const THREAD_SIDEBAR_MIN_WIDTH = 13 * 16;
const THREAD_MAIN_CONTENT_MIN_WIDTH = 40 * 16;
function GlobalDocumentsPanel() {
  const open = useDocumentsPanelStore((state) => state.open);
  const environmentId = useDocumentsPanelStore((state) => state.environmentId);
  const projectId = useDocumentsPanelStore((state) => state.projectId);
  const projectCwd = useDocumentsPanelStore((state) => state.projectCwd);
  const closePanel = useDocumentsPanelStore((state) => state.closePanel);
  if (environmentId === null || projectId === null) return null;
  return (
    <DocumentsPanel
      key={`${environmentId}:${projectId}`}
      open={open}
      environmentId={environmentId}
      projectId={projectId}
      projectCwd={projectCwd ?? undefined}
      onClose={closePanel}
    />
  );
}

export function AppSidebarLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      syncShortcutModifierStateFromKeyboardEvent(event);
    };
    const onWindowKeyUp = (event: KeyboardEvent) => {
      syncShortcutModifierStateFromKeyboardEvent(event);
    };
    const onWindowBlur = () => {
      clearShortcutModifierState();
    };

    window.addEventListener("keydown", onWindowKeyDown, true);
    window.addEventListener("keyup", onWindowKeyUp, true);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, true);
      window.removeEventListener("keyup", onWindowKeyUp, true);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, []);

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action === "open-settings") {
        void navigate({ to: "/settings" });
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate]);

  return (
    <SidebarProvider className="h-dvh! min-h-0!" defaultOpen>
      <Sidebar
        side="left"
        collapsible="offcanvas"
        className="border-r border-border bg-card text-foreground"
        resizable={{
          minWidth: THREAD_SIDEBAR_MIN_WIDTH,
          shouldAcceptWidth: ({ nextWidth, wrapper }) =>
            wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
          storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
        }}
      >
        <ThreadSidebar />
        <SidebarRail />
      </Sidebar>
      {children}
      <GlobalDocumentsPanel />
      <SubAgentPanel />
      <OperatorEntryBanner />
    </SidebarProvider>
  );
}
